import {
  MediaApi,
  MediaUploadRequest,
  MediaUploadResponse,
  MediaAssetResponse,
  MediaSearchResponse,
  MediaDownloadResponse,
  MediaTagResponse,
  CreateMediaTagRequest,
  SetPrimaryProfilePhotoRequest,
  ConfirmUploadRequest,
  SearchMediaAssetsTagMatchEnum,
  MediaExportCreateResponse,
  MediaExportJobResponse,
  MediaExportDownloadResponse
} from 'we-tree-api-client/v2';

import { getApiConfiguration } from '@/services/SessionService';
import { ApiResult, withApiSetup, safeApiCallWithRetry } from './apiWrapper';
import { WeTreeApiError } from './errors';

export type { MediaTagResponse, MediaExportCreateResponse, MediaExportJobResponse, MediaExportDownloadResponse };

export interface PrimaryProfilePhotoResponse {
  mediaAssetId: string | null;
}

/** Result of resolving primary profile photo for safe display (Phase 2). */
export interface PrimaryProfilePhotoDisplay {
  /** URL to use for <img src> when displayStatus is 'safe'. */
  url: string | null;
  /** 'safe' = show image; 'processing' = scan/moderation pending; 'unavailable' = infected/rejected; null = no photo. */
  displayStatus: 'safe' | 'processing' | 'unavailable' | null;
  /** Optional user-facing reason when unavailable (e.g. video transcode failure). */
  message?: string | null;
}

/**
 * Derive display status when a download URL is not available (or for video placeholders).
 * Video: processing while STATUS is processing/uploading; failed → unavailable with message;
 * ready + approved → safe.
 */
export function displayStatusFromMediaAsset(asset: MediaAssetResponse): {
  displayStatus: 'safe' | 'processing' | 'unavailable';
  message?: string | null;
} {
  const mime = (asset.mimeType ?? '').split(';')[0].trim().toLowerCase();
  const isVideo = mime.startsWith('video/');
  const status = (asset.status ?? '').toLowerCase();
  const scan = (asset.scanStatus ?? '').toLowerCase();
  const mod = (asset.moderationStatus ?? '').toLowerCase();

  if (scan === 'infected' || mod === 'rejected') {
    return { displayStatus: 'unavailable' };
  }

  if (isVideo) {
    if (status === 'processing' || status === 'uploading') {
      return { displayStatus: 'processing', message: 'Processing video…' };
    }
    if (status === 'failed') {
      return {
        displayStatus: 'unavailable',
        message: asset.processingError?.trim() || 'Video processing failed'
      };
    }
    if (status === 'ready' && mod === 'approved') {
      return { displayStatus: 'safe' };
    }
    if (mod === 'pending' || status === 'ready') {
      return { displayStatus: 'processing', message: 'Processing video…' };
    }
    return { displayStatus: 'unavailable', message: 'Video unavailable' };
  }

  const isProcessing = (scan === 'pending' || scan === '') && (mod === 'pending' || mod === '');
  if (isProcessing) {
    return { displayStatus: 'processing' };
  }
  return { displayStatus: 'unavailable' };
}

/**
 * Wrapped media API with standardized error handling
 */
class WrappedMediaApi {
  async createUploadRequest(familyId: string, body: MediaUploadRequest): Promise<ApiResult<MediaUploadResponse>> {
    return withApiSetup(
      (config) => new MediaApi(config),
      getApiConfiguration,
      (api) => api.createUploadRequest(familyId, body)
    );
  }

  async uploadLocalMediaFile(familyId: string, mediaAssetId: string, file: File): Promise<ApiResult<void>> {
    const config = await getApiConfiguration();
    if (!config?.basePath) {
      return { success: false, error: new WeTreeApiError('CONFIG_ERROR', 'No API configuration', 0) };
    }
    const url = `${config.basePath}/families/${familyId}/media/upload-local/${mediaAssetId}`;
    const formData = new FormData();
    formData.append('file', file);
    try {
      const token = config.accessToken;
      const headers: Record<string, string> = {};
      if (token) headers['Authorization'] = `Bearer ${token}`;
      const response = await fetch(url, {
        method: 'POST',
        body: formData,
        headers
      });
      if (!response.ok) {
        const errData = (await response.json().catch(() => ({}))) as { message?: string };
        return {
          success: false,
          error: new WeTreeApiError('HTTP_ERROR', errData?.message || response.statusText, response.status)
        };
      }
      return { success: true, data: undefined };
    } catch (e) {
      return {
        success: false,
        error:
          e instanceof WeTreeApiError
            ? e
            : new WeTreeApiError('NETWORK_ERROR', (e as Error)?.message || 'Upload failed', 0, undefined, e)
      };
    }
  }

  async confirmUpload(
    familyId: string,
    mediaAssetId: string,
    tagSlug?: string
  ): Promise<ApiResult<MediaAssetResponse>> {
    const body: ConfirmUploadRequest | undefined = tagSlug ? { tagSlug } : undefined;
    return withApiSetup(
      (config) => new MediaApi(config),
      getApiConfiguration,
      (api) => api.confirmUpload(familyId, mediaAssetId, body)
    );
  }

  async getMediaAsset(familyId: string, mediaAssetId: string): Promise<ApiResult<MediaAssetResponse>> {
    return withApiSetup(
      (config) => new MediaApi(config),
      getApiConfiguration,
      (api) => api.getMediaAsset(familyId, mediaAssetId)
    );
  }

  /** Search media. Filter by mimeType, status, tagIds, tagMatch (no free-text search). */
  async searchMediaAssets(
    familyId: string,
    params?: {
      mimeType?: string;
      status?: string;
      tagIds?: string[];
      tagMatch?: SearchMediaAssetsTagMatchEnum;
      page?: number;
      pageSize?: number;
    }
  ): Promise<ApiResult<MediaSearchResponse>> {
    const tagIdsStr = params?.tagIds && params.tagIds.length > 0 ? params.tagIds.join(',') : undefined;
    return withApiSetup(
      (config) => new MediaApi(config),
      getApiConfiguration,
      (api) =>
        api.searchMediaAssets(
          familyId,
          params?.mimeType,
          params?.status,
          tagIdsStr,
          params?.tagMatch,
          params?.page,
          params?.pageSize
        )
    );
  }

  /** List media tags for the family (for filter dropdown). */
  async listMediaTags(familyId: string): Promise<ApiResult<MediaTagResponse[]>> {
    return withApiSetup(
      (config) => new MediaApi(config),
      getApiConfiguration,
      (api) => api.listMediaTags(familyId)
    );
  }

  /** Create a new media tag for the family. */
  async createMediaTag(familyId: string, name: string): Promise<ApiResult<MediaTagResponse>> {
    return withApiSetup(
      (config) => new MediaApi(config),
      getApiConfiguration,
      (api) => api.createMediaTag(familyId, { name } as CreateMediaTagRequest)
    );
  }

  /** Add a tag to a media asset. */
  async addTagToMediaAsset(familyId: string, mediaAssetId: string, tagId: string): Promise<ApiResult<void>> {
    return withApiSetup(
      (config) => new MediaApi(config),
      getApiConfiguration,
      (api) => api.addTagToMediaAsset(familyId, mediaAssetId, tagId)
    );
  }

  /** Remove a tag from a media asset. */
  async removeTagFromMediaAsset(familyId: string, mediaAssetId: string, tagId: string): Promise<ApiResult<void>> {
    return withApiSetup(
      (config) => new MediaApi(config),
      getApiConfiguration,
      (api) => api.removeTagFromMediaAsset(familyId, mediaAssetId, tagId)
    );
  }

  /** Soft-delete a media asset. */
  async deleteMediaAsset(familyId: string, mediaAssetId: string): Promise<ApiResult<void>> {
    return withApiSetup(
      (config) => new MediaApi(config),
      getApiConfiguration,
      (api) => api.deleteMediaAsset(familyId, mediaAssetId)
    );
  }

  /**
   * Start a bulk media ZIP export (owner/admin; billing §5.7 carve-out).
   * Available on free when per-asset video/audio playback is frozen.
   */
  async createMediaExport(familyId: string): Promise<ApiResult<MediaExportCreateResponse>> {
    return withApiSetup(
      (config) => new MediaApi(config),
      getApiConfiguration,
      (api) => api.createMediaExport(familyId)
    );
  }

  /** Poll status of a media export job. */
  async getMediaExportStatus(familyId: string, exportJobId: string): Promise<ApiResult<MediaExportJobResponse>> {
    return withApiSetup(
      (config) => new MediaApi(config),
      getApiConfiguration,
      (api) => api.getMediaExportStatus(familyId, exportJobId)
    );
  }

  /** Time-limited download URL for a completed export archive (not per-asset playback). */
  async getMediaExportDownloadUrl(
    familyId: string,
    exportJobId: string
  ): Promise<ApiResult<MediaExportDownloadResponse>> {
    return withApiSetup(
      (config) => new MediaApi(config),
      getApiConfiguration,
      (api) => api.getMediaExportDownloadUrl(familyId, exportJobId)
    );
  }

  /** Set an existing media asset as the primary profile photo for a family member. */
  async setPrimaryProfilePhoto(
    familyId: string,
    familyMemberId: string,
    mediaAssetId: string
  ): Promise<ApiResult<void>> {
    return withApiSetup(
      (config) => new MediaApi(config),
      getApiConfiguration,
      (api) =>
        api.setPrimaryProfilePhoto(familyId, familyMemberId, {
          mediaAssetId
        } as SetPrimaryProfilePhotoRequest)
    );
  }

  async approveMedia(familyId: string, mediaAssetId: string): Promise<ApiResult<MediaAssetResponse>> {
    return withApiSetup(
      (config) => new MediaApi(config),
      getApiConfiguration,
      (api) => api.approveMedia(familyId, mediaAssetId)
    );
  }

  async rejectMedia(familyId: string, mediaAssetId: string): Promise<ApiResult<MediaAssetResponse>> {
    return withApiSetup(
      (config) => new MediaApi(config),
      getApiConfiguration,
      (api) => api.rejectMedia(familyId, mediaAssetId)
    );
  }

  async getPrimaryProfilePhotoMediaAssetId(
    familyId: string,
    familyMemberId: string
  ): Promise<ApiResult<PrimaryProfilePhotoResponse>> {
    const config = await getApiConfiguration();
    if (!config?.basePath) {
      return { success: false, error: new WeTreeApiError('CONFIG_ERROR', 'No API configuration', 0) };
    }
    const url = `${config.basePath}/families/${familyId}/media/primary-profile-photo/${familyMemberId}`;
    try {
      const token = config.accessToken;
      const headers: Record<string, string> = { Accept: 'application/json' };
      if (token) headers['Authorization'] = `Bearer ${token}`;
      const response = await fetch(url, { method: 'GET', headers });
      if (!response.ok) {
        const errData = (await response.json().catch(() => ({}))) as { message?: string };
        return {
          success: false,
          error: new WeTreeApiError('HTTP_ERROR', errData?.message || response.statusText, response.status)
        };
      }
      const data = (await response.json()) as PrimaryProfilePhotoResponse;
      return { success: true, data };
    } catch (e) {
      return {
        success: false,
        error:
          e instanceof WeTreeApiError
            ? e
            : new WeTreeApiError('NETWORK_ERROR', (e as Error)?.message || 'Request failed', 0, undefined, e)
      };
    }
  }

  async getDownloadUrl(
    familyId: string,
    mediaAssetId: string,
    size?: 'original' | '40x40' | '200x200' | '800x800' | 'poster'
  ): Promise<ApiResult<MediaDownloadResponse>> {
    return withApiSetup(
      (config) => new MediaApi(config),
      getApiConfiguration,
      (api) => api.getDownloadUrl(familyId, mediaAssetId, size)
    );
  }

  /**
   * Best-effort video poster URL (`size=poster`). Returns null when no poster exists
   * (404 / missing STORAGE_KEY_POSTER) so the UI can keep the film-icon fallback.
   */
  async getVideoPosterUrl(familyId: string, mediaAssetId: string): Promise<string | null> {
    const downloadResult = await this.getDownloadUrl(familyId, mediaAssetId, 'poster');
    if (!downloadResult.success || !downloadResult.data?.url) {
      return null;
    }
    let url = downloadResult.data.url;
    if (!url.startsWith('http')) {
      const config = await getApiConfiguration();
      if (config?.basePath) {
        const origin = config.basePath.replace(/\/v2\/?$/, '');
        url = `${origin}${url}`;
      }
    }
    return url;
  }

  /**
   * Returns URL and display status for any media asset (safe display for grid etc.).
   * Same semantics as getPrimaryProfilePhotoDisplay: safe = show image, processing = pending, unavailable = infected/rejected.
   */
  async getMediaDisplay(
    familyId: string,
    mediaAssetId: string,
    size: '40x40' | '200x200' | '800x800' | 'original' | 'poster' = '200x200'
  ): Promise<ApiResult<PrimaryProfilePhotoDisplay>> {
    const downloadResult = await this.getDownloadUrl(familyId, mediaAssetId, size);
    if (downloadResult.success && downloadResult.data) {
      let url = downloadResult.data.url;
      if (!url.startsWith('http')) {
        const config = await getApiConfiguration();
        if (config?.basePath) {
          const origin = config.basePath.replace(/\/v2\/?$/, '');
          url = `${origin}${url}`;
        }
      }
      return { success: true, data: { url, displayStatus: 'safe' } };
    }
    const assetResult = await this.getMediaAsset(familyId, mediaAssetId);
    if (!assetResult.success || !assetResult.data) {
      return { success: true, data: { url: null, displayStatus: 'unavailable' } };
    }
    const derived = displayStatusFromMediaAsset(assetResult.data);
    // Download already failed; never report 'safe' without a URL.
    const displayStatus = derived.displayStatus === 'safe' ? 'unavailable' : derived.displayStatus;
    return {
      success: true,
      data: { url: null, displayStatus, message: derived.message }
    };
  }

  /**
   * Returns URL and display status for the primary profile photo (safe display).
   * - displayStatus 'safe': show image at url.
   * - 'processing': scan/moderation pending (show "Processing").
   * - 'unavailable': infected or rejected (show "Unavailable").
   * - null: no primary photo (show placeholder).
   * For local dev (relative /file URL), url is prepended with API origin when safe.
   */
  async getPrimaryProfilePhotoDisplay(
    familyId: string,
    familyMemberId: string,
    size: '40x40' | '200x200' | '800x800' | 'original' = '200x200'
  ): Promise<ApiResult<PrimaryProfilePhotoDisplay>> {
    const primaryResult = await this.getPrimaryProfilePhotoMediaAssetId(familyId, familyMemberId);
    if (!primaryResult.success) return { success: false, error: primaryResult.error };
    const mediaAssetId = primaryResult.data?.mediaAssetId ?? null;
    if (!mediaAssetId) {
      return { success: true, data: { url: null, displayStatus: null } };
    }
    const downloadResult = await this.getDownloadUrl(familyId, mediaAssetId, size);
    if (downloadResult.success && downloadResult.data) {
      let url = downloadResult.data.url;
      if (!url.startsWith('http')) {
        const config = await getApiConfiguration();
        if (config?.basePath) {
          const origin = config.basePath.replace(/\/v2\/?$/, '');
          url = `${origin}${url}`;
        }
      }
      return { success: true, data: { url, displayStatus: 'safe' } };
    }
    // No URL (not safe to serve): determine processing vs unavailable from asset status
    const assetResult = await this.getMediaAsset(familyId, mediaAssetId);
    if (!assetResult.success || !assetResult.data) {
      return { success: true, data: { url: null, displayStatus: 'unavailable' } };
    }
    const derived = displayStatusFromMediaAsset(assetResult.data);
    const displayStatus = derived.displayStatus === 'safe' ? 'unavailable' : derived.displayStatus;
    return {
      success: true,
      data: { url: null, displayStatus, message: derived.message }
    };
  }

  /**
   * Full profile picture upload: createUploadRequest -> PUT or POST file -> confirmUpload
   */
  async uploadProfilePicture(
    familyId: string,
    familyMemberId: string,
    file: File
  ): Promise<ApiResult<MediaAssetResponse>> {
    return this.uploadProfilePictureWithProgress(familyId, familyMemberId, file);
  }

  /**
   * Upload an image for citation evidence (family-level, no person association).
   * Use when adding a citation and linking an image (birth certificate, census record, etc.).
   */
  async uploadCitationImage(
    familyId: string,
    file: File,
    onProgress?: (percent: number, stage: 'request' | 'upload' | 'confirm') => void
  ): Promise<ApiResult<MediaAssetResponse>> {
    return this.uploadFamilyMedia(familyId, file, onProgress, 'citation');
  }

  /**
   * Upload family-level media (no person association). Supports image/video/audio per API rules.
   * @param tagSlug Optional auto-tag after confirm (e.g. "citation"). Omit for general library uploads.
   */
  async uploadFamilyMedia(
    familyId: string,
    file: File,
    onProgress?: (percent: number, stage: 'request' | 'upload' | 'confirm') => void,
    tagSlug?: string
  ): Promise<ApiResult<MediaAssetResponse>> {
    onProgress?.(5, 'request');
    const createResult = await safeApiCallWithRetry(async () => {
      const r = await withApiSetup(
        (config) => new MediaApi(config),
        getApiConfiguration,
        (api) =>
          api.createUploadRequest(familyId, {
            fileName: file.name,
            fileSize: file.size,
            contentType: file.type || 'application/octet-stream'
          })
      );
      if (!r.success)
        throw (r as { error: WeTreeApiError }).error ?? new WeTreeApiError('CONFIG_ERROR', 'No upload response', 0);
      if (!r.data) throw new WeTreeApiError('CONFIG_ERROR', 'No upload response', 0);
      return { data: r.data };
    }, 'createUploadRequest');
    if (!createResult.success) {
      return { success: false, error: createResult.error };
    }
    if (!createResult.data) {
      return { success: false, error: new WeTreeApiError('CONFIG_ERROR', 'No upload response data', 0) };
    }
    const { mediaAssetId, upload } = createResult.data;
    onProgress?.(15, 'upload');

    const uploadResult = await this.uploadFileWithProgress(familyId, mediaAssetId, file, upload, (p) =>
      onProgress?.(15 + p * 0.7, 'upload')
    );
    if (!uploadResult.success) {
      return { success: false, error: uploadResult.error };
    }
    onProgress?.(90, 'confirm');

    const confirmResult = await safeApiCallWithRetry(async () => {
      const r = await this.confirmUpload(familyId, mediaAssetId, tagSlug);
      if (!r.success)
        throw (r as { error: WeTreeApiError }).error ?? new WeTreeApiError('CONFIG_ERROR', 'Confirm failed', 0);
      if (!r.data) throw new WeTreeApiError('CONFIG_ERROR', 'Confirm failed', 0);
      return { data: r.data };
    }, 'confirmUpload');
    onProgress?.(100, 'confirm');
    return confirmResult;
  }

  /**
   * Upload profile picture with progress callback and retry support.
   * @param setAsPrimary When true (default), sets as primary profile photo. When false, adds to gallery only.
   * @param onProgress Called with 0-100 (approximate: 10% for create, 10-90% for upload, 90-100% for confirm)
   */
  async uploadProfilePictureWithProgress(
    familyId: string,
    familyMemberId: string,
    file: File,
    onProgress?: (percent: number, stage: 'request' | 'upload' | 'confirm') => void,
    setAsPrimaryProfilePhoto = true
  ): Promise<ApiResult<MediaAssetResponse>> {
    onProgress?.(5, 'request');
    const createResult = await safeApiCallWithRetry(async () => {
      const r = await withApiSetup(
        (config) => new MediaApi(config),
        getApiConfiguration,
        (api) =>
          api.createUploadRequest(familyId, {
            familyMemberId,
            fileName: file.name,
            fileSize: file.size,
            contentType: file.type || 'image/jpeg',
            setAsPrimaryProfilePhoto
          })
      );
      if (!r.success)
        throw (r as { error: WeTreeApiError }).error ?? new WeTreeApiError('CONFIG_ERROR', 'No upload response', 0);
      if (!r.data) throw new WeTreeApiError('CONFIG_ERROR', 'No upload response', 0);
      return { data: r.data };
    }, 'createUploadRequest');
    if (!createResult.success) {
      return { success: false, error: createResult.error };
    }
    if (!createResult.data) {
      return { success: false, error: new WeTreeApiError('CONFIG_ERROR', 'No upload response data', 0) };
    }
    const { mediaAssetId, upload } = createResult.data;
    onProgress?.(15, 'upload');

    const uploadResult = await this.uploadFileWithProgress(familyId, mediaAssetId, file, upload, (p) =>
      onProgress?.(15 + p * 0.7, 'upload')
    );
    if (!uploadResult.success) {
      return { success: false, error: uploadResult.error };
    }
    onProgress?.(90, 'confirm');

    const confirmResult = await safeApiCallWithRetry(async () => {
      const r = await this.confirmUpload(familyId, mediaAssetId, 'profile');
      if (!r.success)
        throw (r as { error: WeTreeApiError }).error ?? new WeTreeApiError('CONFIG_ERROR', 'Confirm failed', 0);
      if (!r.data) throw new WeTreeApiError('CONFIG_ERROR', 'Confirm failed', 0);
      return { data: r.data };
    }, 'confirmUpload');
    onProgress?.(100, 'confirm');
    return confirmResult;
  }

  /** Upload file to presigned URL or local endpoint with progress. */
  private async uploadFileWithProgress(
    _familyId: string,
    _mediaAssetId: string,
    file: File,
    upload: { url: string; method: string; headers?: Record<string, string> },
    onProgress?: (percent: number) => void
  ): Promise<ApiResult<void>> {
    return new Promise((resolve) => {
      const configPromise = getApiConfiguration();
      const xhr = new XMLHttpRequest();
      xhr.upload.addEventListener('progress', (e) => {
        if (e.lengthComputable && onProgress) {
          onProgress((e.loaded / e.total) * 100);
        }
      });
      xhr.addEventListener('load', async () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          resolve({ success: true, data: undefined });
        } else {
          let message = xhr.statusText;
          try {
            const errBody = JSON.parse(xhr.responseText) as { message?: string };
            message = errBody?.message ?? message;
          } catch {
            // ignore
          }
          resolve({
            success: false,
            error: new WeTreeApiError('HTTP_ERROR', message, xhr.status)
          });
        }
      });
      xhr.addEventListener('error', () => {
        resolve({
          success: false,
          error: new WeTreeApiError('NETWORK_ERROR', 'Upload failed', 0)
        });
      });
      xhr.addEventListener('abort', () => {
        resolve({
          success: false,
          error: new WeTreeApiError('NETWORK_ERROR', 'Upload cancelled', 0)
        });
      });

      if (upload.method === 'POST') {
        configPromise.then(async (config) => {
          if (!config?.basePath) {
            resolve({ success: false, error: new WeTreeApiError('CONFIG_ERROR', 'No API configuration', 0) });
            return;
          }
          const url = upload.url.startsWith('http')
            ? upload.url
            : `${config.basePath}${upload.url.startsWith('/') ? '' : '/'}${upload.url}`;
          const formData = new FormData();
          formData.append('file', file);
          xhr.open('POST', url);
          if (config.accessToken) {
            xhr.setRequestHeader('Authorization', `Bearer ${config.accessToken}`);
          }
          xhr.send(formData);
        });
      } else {
        const headers: Record<string, string> = {
          ...(upload.headers || {}),
          'Content-Type': file.type || 'image/jpeg'
        };
        xhr.open('PUT', upload.url);
        Object.entries(headers).forEach(([k, v]) => xhr.setRequestHeader(k, v));
        xhr.send(file);
      }
    });
  }
}

export const mediaApi = new WrappedMediaApi();
export default mediaApi;
